/* eslint-disable no-param-reassign */
import React, { Component } from 'react';

/** UI Kit */
import { TxtDefault } from '@atresmedia/intranet-texts';

import moment from 'moment';
import Icon from '../../../core/Icon';
import { Ok, Exclamation } from '../../../../../assets/images/index';

/** Core */
import Label from '../../../core/Label';
import Input from '../../../core/Input';
import InputTime from '../../../core/InputTime';
import Buttons from '../../../core/Buttons';

/** Styles */
import { StyledFormControl } from './style';
import InputDate from '../../../core/InputDate';

// Todas las marcas horarias del formulario, en orden cronologico
const TIME_KEYS = [
  'accessTime',
  'startBreakTime',
  'endBreakTime',
  'startBreakTime2',
  'endBreakTime2',
  'startBreakTime3',
  'endBreakTime3',
  'startBreakTime4',
  'endBreakTime4',
  'exitTime'
];

// Normaliza a cadena ISO UTC solo para COMPARAR. El estado guarda objetos
const toIso = value =>
  value ? `${moment.utc(value).format('YYYY-MM-DDTHH:mm:ss')}Z` : null;

// Traslada una marca horaria al dia indicado conservando la hora
const moveToDay = (time, day) =>
  moment
    .utc(time)
    .year(day.year())
    .month(day.month())
    .date(day.date());

// Pasa un fichaje del backend (o null si el dia no tiene) a estado del formulario
const imputationToState = imputation => {
  const data = imputation || {};
  const state = {
    // Registro original: al guardar se envia para actualizarlo (PUT) sin volver a pedirlo
    imputation: imputation || null,
    existing: Boolean(imputation),
    comment: data.comment || ''
  };
  TIME_KEYS.forEach(key => {
    state[key] = data[key] ? moment.utc(data[key]) : null;
  });
  const pausasConDatos = [
    'startBreakTime',
    'startBreakTime2',
    'startBreakTime3',
    'startBreakTime4'
  ].filter(key => data[key]).length;
  state.visibleBreaks = Math.max(pausasConDatos, 1);
  return state;
};

// Los inputs de entrada y salida usan un `type` historico que no coincide
// con la clave del estado; el resto (pausas) ya coinciden.
const TYPE_TO_KEY = {
  accHour: 'accessTime',
  extHour: 'exitTime'
};

// Etiquetas para formar los mensajes de error
const LABELS = {
  accessTime: 'la hora de entrada',
  startBreakTime: 'el inicio de la primera pausa',
  endBreakTime: 'la vuelta de la primera pausa',
  startBreakTime2: 'el inicio de la segunda pausa',
  endBreakTime2: 'la vuelta de la segunda pausa',
  startBreakTime3: 'el inicio de la tercera pausa',
  endBreakTime3: 'la vuelta de la tercera pausa',
  startBreakTime4: 'el inicio de la cuarta pausa',
  endBreakTime4: 'la vuelta de la cuarta pausa',
  exitTime: 'la hora de salida'
};

// Funcion para comprobar que las horas que se envian tienen sentido
const validateTimes = state => {
  const ultimo = TIME_KEYS.length - 1; // exitTime
  let previoKey = null;
  for (let i = 0; i < TIME_KEYS.length; i += 1) {
    const key = TIME_KEYS[i];
    if (state[key]) {
      const anterior = TIME_KEYS[i - 1];
      // Las pausas se rellenan en orden, sin saltarse ninguna.
      if (i > 0 && i < ultimo && !state[anterior]) {
        return `No se puede introducir ${LABELS[key]} si antes no ha rellenado ${LABELS[anterior]}.`;
      }
      // La salida solo exige la entrada: las pausas son opcionales.
      if (i === ultimo && !state[TIME_KEYS[0]]) {
        return `No se puede introducir ${
          LABELS[key]
        } si antes no ha rellenado ${LABELS[TIME_KEYS[0]]}.`;
      }
      // Una pausa no puede durar cero: su vuelta debe ser estrictamente
      // posterior a su inicio. Entre marcas distintas si se admite la
      // igualdad, para poder encadenar tramos sin hueco.
      if (previoKey) {
        const cierraPausa =
          key.startsWith('endBreakTime') &&
          previoKey === key.replace('end', 'start');
        // Se normalizan ambos lados: comparar una cadena con un objeto
        // moment da NaN y toda comparacion resulta false, dejando pasar
        // datos invalidos sin avisar.
        const actual = toIso(state[key]);
        const previo = toIso(state[previoKey]);
        const invalido = cierraPausa ? actual <= previo : actual < previo;
        if (invalido) {
          return `${LABELS[key]} debe ser posterior a ${LABELS[previoKey]}.`;
        }
      }
      previoKey = key;
    }
  }
  return null;
};

class FormControl extends Component {
  constructor(props) {
    super(props);

    // El contenedor carga el fichaje de hoy; llega {} si hoy no hay
    const { currentDayImputation, postResult } = this.props;
    const hoy =
      currentDayImputation && Object.keys(currentDayImputation).length
        ? currentDayImputation
        : null;
    this.state = {
      postResult,
      workDay: moment(),
      loadingDay: false,
      loadKey: 0,
      ...imputationToState(hoy)
    };
  }

  // Al cambiar el dia se carga el fichaje guardado para ese dia (o se vacia
  // el formulario si no tiene), para que lo mostrado coincida con el servidor
  handleWorkDayChange = value => {
    const workDay = moment(value, 'DD-MM-YYYY');
    if (!workDay.isValid()) {
      return;
    }

    const { onLoadDay } = this.props;
    const date = workDay.format('YYYY-MM-DD');
    this.setState({
      ...imputationToState(null),
      workDay,
      loadingDay: true,
      postResult: { success: false, show: false, text: '' }
    });

    onLoadDay(date)
      .then(imputation => {
        // Si el usuario ya ha cambiado a otro dia, esta respuesta llega tarde
        if (this.state.workDay.format('YYYY-MM-DD') !== date) {
          return;
        }
        this.setState(prev => ({
          ...imputationToState(imputation),
          loadingDay: false,
          loadKey: prev.loadKey + 1
        }));
      })
      .catch(() => {
        if (this.state.workDay.format('YYYY-MM-DD') !== date) {
          return;
        }
        this.setState({
          loadingDay: false,
          postResult: {
            success: false,
            show: true,
            text: 'No se pudo cargar el fichaje de ese día.'
          }
        });
      });
  };

  // Funcion para mostrar mas pausas
  handleAddBreak = () => {
    this.setState(prev => ({
      visibleBreaks: Math.min(prev.visibleBreaks + 1, 4)
    }));
  };

  // Valida el conjunto de horas y, si es correcto, envia la jornada entera
  handleSubmit = () => {
    const error = validateTimes(this.state);
    if (error) {
      this.setState({
        postResult: { success: false, show: true, text: error }
      });
      return;
    }

    const { onSubmit } = this.props;
    const { comment, workDay, imputation } = this.state;
    const horas = {};
    TIME_KEYS.forEach(key => {
      horas[key] = this.state[key];
    });

    const date = workDay.format('YYYY-MM-DD');
    Promise.resolve(onSubmit({ ...horas, comment, date }, imputation)).then(
      saved => {
        if (!saved) {
          this.setState({
            postResult: {
              success: false,
              show: true,
              text: 'Error: No se pudo guardar su imputación.'
            }
          });
          return;
        }
        // El formulario se queda con lo guardado, que ya es lo que hay en el
        // servidor para ese dia; a partir de ahora se esta modificando
        this.setState({
          imputation: saved,
          existing: true,
          postResult: {
            success: true,
            show: true,
            text: 'Su imputación se guardó correctamente.'
          }
        });
      }
    );
  };

  // Guarda en el estado el día y las horas elegidas y avisa al momento si el orden falla.
  handleHours = (value, type) => {
    if (type === 'ob') {
      this.setState({ comment: value });
      return;
    }

    const { workDay } = this.state;
    const key = TYPE_TO_KEY[type] || type;
    // InputTime genera la marca con la fecha de HOY: se reubica en el dia elegido
    const marca = value ? moveToDay(value, workDay) : value;

    this.setState({ [key]: marca }, () => {
      // eslint-disable-next-line react/no-access-state-in-setstate
      const error = validateTimes(this.state);
      this.setState({
        postResult: error
          ? { success: false, show: true, text: error }
          : { success: false, show: false, text: '' }
      });
    });
  };

  render() {
    const {
      workDay,
      loadingDay,
      loadKey,
      existing,
      visibleBreaks,
      postResult,
      accessTime,
      exitTime,
      comment
    } = this.state;
    const ORDINALES = { 1: 'primera', 2: 'segunda', 3: 'tercera', 4: 'cuarta' };
    return (
      <StyledFormControl>
        <div className="form-access">
          <div className="wrapper-time-flex">
            <div className="time-input-only">
              <Label label="Día de la jornada" className="mb-5" />
              <InputDate
                format="DD-MM-YYYY"
                value={workDay}
                max={new Date()}
                onChange={this.handleWorkDayChange}
              />
            </div>
            <div className="time-input-only">
              <Label
                label="Hora de entrada / Inicio de la jornada"
                className="mb-5"
              />
              <InputTime
                key={loadKey}
                onChange={e => this.handleHours(e, 'accHour')}
                value={accessTime}
              />
            </div>
            {[1, 2, 3, 4].slice(0, visibleBreaks).map(n => {
              const startKey =
                n === 1 ? 'startBreakTime' : `startBreakTime${n}`;
              const endKey = n === 1 ? 'endBreakTime' : `endBreakTime${n}`;
              return (
                <div className="time-row" key={startKey}>
                  <div className="time-input">
                    <Label
                      label={`Hora de inicio de ${ORDINALES[n]} pausa (comer, ausencia, etc...)`}
                      className="mb-5"
                    />
                    <InputTime
                      key={loadKey}
                      onChange={e => this.handleHours(e, startKey)}
                      value={this.state[startKey]}
                    />
                  </div>
                  <div className="time-input">
                    <Label
                      label={`Hora de vuelta de la ${ORDINALES[n]} pausa`}
                      className="mb-5"
                    />
                    <InputTime
                      key={loadKey}
                      onChange={e => this.handleHours(e, endKey)}
                      value={this.state[endKey]}
                    />
                  </div>
                </div>
              );
            })}

            {visibleBreaks < 4 && (
              <Buttons dark onClick={this.handleAddBreak}>
                Añadir pausa
              </Buttons>
            )}
            <div className="time-input-only">
              <Label
                label="Hora de salida / Fin de la jornada"
                htmlFor="end"
                className="mb-5"
              />
              <InputTime
                key={loadKey}
                onChange={e => this.handleHours(e, 'extHour')}
                value={exitTime}
              />
            </div>
            <div>
              <Label
                label="Observaciones"
                htmlFor="observations"
                className="mb-5"
              />
              <Input
                multiline
                id="observations"
                name="observations"
                className="mb-15"
                onChange={e => this.handleHours(e, 'ob')}
                value={comment}
              />
            </div>
          </div>

          {loadingDay && (
            <TxtDefault className="mb-10">
              Cargando fichaje del día...
            </TxtDefault>
          )}
          {!loadingDay && existing && (
            <TxtDefault className="mb-10">
              Este día ya tiene un fichaje registrado: estás modificándolo.
            </TxtDefault>
          )}

          <Buttons
            secondary
            className="fr"
            type="submit"
            small
            disabled={loadingDay || validateTimes(this.state)}
            onClick={this.handleSubmit}
          >
            Guardar jornada
          </Buttons>

          {postResult && postResult.show && (
            <TxtDefault
              className={
                postResult.success === true ? `form-success` : `form-error`
              }
            >
              <Icon
                size="m"
                svg={postResult.success === true ? Ok : Exclamation}
                color={postResult.success === true ? `success` : `error`}
                className="mr-10 pt-10"
              />
              {postResult.text}
            </TxtDefault>
          )}
        </div>
      </StyledFormControl>
    );
  }
}

export default FormControl;
